import type { ReactNode } from 'react'

import { cn } from '../../lib/utils'

// アラートの見た目の種類を定義 (今回は'warning'のみ)
type AlertVariant = 'default' | 'destructive' | 'warning'

interface AlertProps {
  /**
   * コンポーネントに適用する追加のCSSクラス
   */
  className?: string
  /**
   * アラートの見た目の種類。'warning'を指定すると黄色いスタイルになります。
   * @default 'default'
   */
  variant?: AlertVariant
  /**
   * アラート内に表示するコンテンツ (子要素)
   */
  children: ReactNode
}

// スタイルをvariantごとに定義しておく
const variantStyles: Record<AlertVariant, string> = {
  default: 'bg-background text-foreground',
  destructive: 'border-red-200 bg-red-50 text-destructive dark:border-red-900',
  warning: 'border-yellow-200 bg-yellow-50 text-yellow-900 dark:border-yellow-800',
}

/**
 * ユーザーの注意を引くための、目立つメッセージボックスを表示するコンポーネント
 */
export function Alert({ className, variant = 'default', children }: AlertProps) {
  return (
    <div
      className={cn(
        'relative w-full rounded-lg border p-4 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:top-4 [&>svg]:left-4 [&>svg]:text-foreground [&>svg~*]:pl-7',
        variantStyles[variant], // variantに応じたスタイルを適用
        className
      )}
      role="alert"
    >
      {children}
    </div>
  )
}

/**
 * Alertコンポーネント内で使用する、太字のタイトル部分
 */
export function AlertTitle({ className, children }: { className?: string; children: ReactNode }) {
  return <h5 className={cn('mb-1 font-bold leading-none tracking-tight', className)}>{children}</h5>
}

/**
 * Alertコンポーネント内で使用する、説明文の部分
 */
export function AlertDescription({
  className,
  children,
}: {
  className?: string
  children: ReactNode
}) {
  return <div className={cn('text-sm [&_p]:leading-relaxed', className)}>{children}</div>
}
